import { ChangeDetectionStrategy, Component, OnDestroy, OnInit } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { catchError, map, switchMap, takeUntil } from 'rxjs/operators';
import { EligibilityDetailDialogComponent } from '../../components/eligibility-detail-dialog/eligibility-detail-dialog.component';
import { PreferredPartnerService } from '../../preferred-partner.service';
import { CurrentWeekResponse, EligibilityResponse, EligibilityWeek, SummaryResponse } from '../../types';

interface OverviewVm {
  summary: SummaryResponse | null;
  eligibility: EligibilityResponse | null;
  currentWeek: CurrentWeekResponse | null;
}

/**
 * /preferred-partner landing page. Shows:
 *   - one card per region (current-week eligibility status from /summary),
 *   - the table of the last 4 weekly verdicts from /eligibility?weeks=4,
 *   - a dialog with the full effectiveBreakdowns + override badge.
 */
@Component({
  selector: 'lohi-ppp-overview',
  templateUrl: './ppp-overview.component.html',
  styleUrls: ['./ppp-overview.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PppOverviewComponent implements OnInit, OnDestroy {
  public readonly vm$: Observable<OverviewVm>;

  private destroy$$ = new Subject<void>();

  constructor(private pppService: PreferredPartnerService, private dialog: MatDialog) {
    this.vm$ = combineLatest([
      this.pppService.summary$,
      // We want the cron-stamped verdicts: 4-week history.
      this.pppService.currentWeek$.pipe(
        switchMap(() => this.pppService.listEligibility$({ weeks: 4 })),
        catchError(() => of<EligibilityResponse>({ weeks: [] })),
      ),
      this.pppService.currentWeek$,
    ]).pipe(map(([summary, eligibility, currentWeek]) => ({ summary, eligibility, currentWeek })));
  }

  public ngOnInit(): void {
    this.vm$.pipe(takeUntil(this.destroy$$)).subscribe();
  }

  public ngOnDestroy(): void {
    this.destroy$$.next();
    this.destroy$$.complete();
  }

  public openWeek(week: EligibilityWeek): void {
    this.dialog.open(EligibilityDetailDialogComponent, {
      data: { week },
      width: '640px',
      autoFocus: false,
    });
  }

  public trackByRegionId(index: number, item: { regionId: number }): number {
    return item.regionId;
  }

  public trackByWeekId(index: number, item: EligibilityWeek): number {
    return item.id;
  }
}
